import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SUB_TONE = {
  ACTIVE: "bg-emerald-50 text-emerald-700",
  TRIALING: "bg-blue-50 text-blue-700",
  PAST_DUE: "bg-amber-50 text-amber-700",
  CANCELLED: "bg-rose-50 text-rose-700",
  NONE: "bg-slate-100 text-slate-600",
};

export default function Organizations() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");

  useEffect(() => {
    api
      .get("/platform/organizations", {
        params: { q: q || undefined, status: status === "ALL" ? undefined : status },
      })
      .then(({ data }) => setRows(data));
  }, [q, status]);

  return (
    <div className="space-y-6" data-testid="platform-orgs-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Organizations</h1>
        <p className="mt-1 text-sm text-slate-500">
          {rows.length} tenant{rows.length === 1 ? "" : "s"} — assign plans, toggle modules, suspend accounts
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="orgs-search"
            className="h-9 pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, GSTIN or email"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-full sm:w-44" data-testid="orgs-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All accounts</SelectItem>
            <SelectItem value="ACTIVE">Active only</SelectItem>
            <SelectItem value="SUSPENDED">Suspended only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="orgs-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Organisation</th>
              <th className="px-3 py-2.5 text-left font-semibold">GSTIN</th>
              <th className="px-3 py-2.5 text-left font-semibold">Plan</th>
              <th className="px-3 py-2.5 text-left font-semibold">Subscription</th>
              <th className="px-3 py-2.5 text-right font-semibold">Users</th>
              <th className="px-3 py-2.5 text-right font-semibold">Invoices</th>
              <th className="px-3 py-2.5 text-left font-semibold">Joined</th>
              <th className="px-4 py-2.5 text-right font-semibold">Account</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`org-row-${o.id}`}>
                <td className="px-4 py-2.5">
                  <Link
                    to={`/platform/organizations/${o.id}`}
                    className="font-medium text-violet-700 hover:text-violet-900"
                    data-testid={`org-link-${o.id}`}
                  >
                    {o.name}
                  </Link>
                  <div className="text-[11px] text-slate-500">{o.email || "—"}</div>
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">{o.gstin || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{o.subscription?.plan_name || "—"}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      SUB_TONE[o.subscription?.status] || SUB_TONE.NONE
                    }`}
                  >
                    {o.subscription?.status || "NONE"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{o.user_count}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{o.invoice_count}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-2.5 text-right">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      o.status === "SUSPENDED" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {o.status}
                  </span>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={8} className="px-4 py-14 text-center text-sm text-slate-500">
                  No organisations match
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
